import React, {
  useCallback, useMemo, useState,
} from "react"
import {
  Box, Flex, Text,
} from "@chakra-ui/react"
import { Control, useController } from "react-hook-form"
import { LexicalComposer } from "@lexical/react/LexicalComposer"
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin"
import { ContentEditable } from "@lexical/react/LexicalContentEditable"
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary"
import { $getSelection, $isRangeSelection } from "lexical"
import {
  $createMergeTagNode,
  createMergeTagEditorConfig,
  DragDropPlugin,
  findLabel,
  MergeFieldInsertPopover,
  MergeTagInitPlugin,
  MergeTagKeyboardPlugin,
  MergeTagOnChangePlugin,
  SMSEditorContext,
} from "components/MergeTagLexical"
import { useForms } from "queries/forms"
import { getFormsGroupedByType } from "utilities/forms"
import { DioboxEvent } from "sharedTypes"

export { SMSEditorContext } from "components/MergeTagLexical"

// ---------------------------------------------------------------------------
// Inline plugins (small, only used here)
// ---------------------------------------------------------------------------

type ToolbarProps = {
  event: DioboxEvent
  forms: { externalId: string; name: string }[]
}

const Toolbar = ({ event, forms }: ToolbarProps) => {
  const [editor] = useLexicalComposerContext()

  const insertTag = (id: string, label: string) => {
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        selection.insertNodes([$createMergeTagNode(id, label)])
      }
    })
    editor.focus()
  }

  return (
    <Flex bg="gray.50" px={2} py={1}>
      <MergeFieldInsertPopover
        event={event}
        draggable
        onInsert={(tagId) => insertTag(tagId, findLabel(tagId, forms))}
      />
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type Props = {
  name: string
  control: Control
  characterLimit?: number
  event: DioboxEvent
  onSave?: () => void
}

const SMSEditor = ({
  name, control, characterLimit, event, onSave,
}: Props) => {
  const { field } = useController({ name, control })
  const { data } = useForms(event.id)
  const forms = data?.data ?? []
  const groupedForms = useMemo(() => getFormsGroupedByType(forms, event), [forms, event])
  const initialConfig = useMemo(() => createMergeTagEditorConfig("SMSEditor"), [])

  const [charCount, setCharCount] = useState(() => (field.value || "").length)

  const handleChange = useCallback((plainText: string) => {
    field.onChange(plainText)
    setCharCount(plainText.length)
  }, [field.onChange])

  const remaining = characterLimit ? characterLimit - charCount : null

  const contextValue = useMemo(() => ({ groupedForms, onSave }), [groupedForms, onSave])

  return (
    <SMSEditorContext.Provider value={contextValue}>
      <Box
        borderWidth={1}
        borderRadius="xl"
        overflow="hidden"
        mt={4}
      >
        <LexicalComposer initialConfig={initialConfig}>
          <Toolbar event={event} forms={forms} />
          <Box borderTopWidth={1} borderColor="gray.200" position="relative">
            <PlainTextPlugin
              contentEditable={(
                <ContentEditable
                  style={{
                    minHeight: "120px",
                    padding: "0.75rem",
                    outline: "none",
                    fontSize: "1rem",
                    lineHeight: "1.75",
                    fontFamily: "inherit",
                  }}
                />
              )}
              placeholder={(
                <Text
                  color="gray.400"
                  position="absolute"
                  top="0.75rem"
                  left="0.75rem"
                  pointerEvents="none"
                  fontSize="md"
                >
                  Type your message...
                </Text>
              )}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </Box>
          <HistoryPlugin />
          <MergeTagOnChangePlugin onChange={handleChange} />
          <DragDropPlugin />
          <MergeTagKeyboardPlugin />
          <MergeTagInitPlugin fieldValue={field.value} forms={forms} />
        </LexicalComposer>
      </Box>

      {characterLimit && remaining !== null && (
        <Flex justifyContent="space-between" mt={2}>
          <Text fontSize="sm" color="gray.500">Character limit</Text>
          <Text
            fontSize="sm"
            color={remaining >= 0 ? "gray.500" : "red.500"}
          >
            {remaining}
          </Text>
        </Flex>
      )}
    </SMSEditorContext.Provider>
  )
}

export default SMSEditor
